import { memo, useCallback, useMemo, type FunctionComponent, type ReactNode } from 'react';
import keys from 'lodash/keys';
import find from 'lodash/find';
import size from 'lodash/size';
import trim from 'lodash/trim';
import isString from 'lodash/isString';
import isPlainObject from 'lodash/isPlainObject';
import { useIntl } from 'react-intl';
// Material UI imports
import Typography from '@mui/material/Typography';
// Skillmore UI Components
import { fontWeightBold } from '@empathco/ui-components/src/styles/themeOptions';
import { commonFormattingValues, mapChunks, type Values } from '@empathco/ui-components/src/helpers/intl';
import CardSection from '@empathco/ui-components/src/elements/CardSection';
import StandardLink from '@empathco/ui-components/src/elements/StandardLink';
// local imports
import { AdminJob } from '../graphql/types';
import { Job } from '../models/job';
import { getJobAnalyticsData, useMixpanel } from '../context/analytics';

type RoleCustomDataProps = {
  readonly role?: Job | AdminJob | null;
};

const RoleCustomData: FunctionComponent<RoleCustomDataProps> = ({
  role
}) => {
  const mixpanel = useMixpanel();
  const { formatMessage } = useIntl();

  const { custom_data } = role || {};

  const [customData, linkType, link] = useMemo(() => {
    if (custom_data) {
      try {
        const data = JSON.parse(custom_data) as Values;
        const dataKeys = data && isPlainObject(data) ? keys(data) : null;
        if (size(dataKeys) >= 1) {
          const link_type = find(dataKeys, (lnkType) => size(trim(
            formatMessage({ id: 'role.description.custom_data.links', defaultMessage: ' ' }, { link_type: lnkType })
          )) >= 1);
          return [
            data,
            link_type,
            link_type
              ? formatMessage({ id: 'role.description.custom_data.links', defaultMessage: ' ' }, { link_type })
              : undefined
          ] as const;
        }
      } catch (_err) {
        // no-op
      }
    }
    return [null, undefined, undefined] as const;
  }, [custom_data, formatMessage]);

  const handleLinkClick = useCallback(() => {
    if (role) mixpanel.track('JobDescriptionLink', { ...getJobAnalyticsData(role), link: linkType, href: link });
  }, [mixpanel, role, linkType, link]);

  const message = useMemo(() => {
    if (!customData || size(customData) < 1) return null;
    const msg = formatMessage({ id: 'role.description.custom_data.text', defaultMessage: ' ' }, {
      ...customData,
      ...commonFormattingValues,
      // eslint-disable-next-line react/no-unstable-nested-components
      em: (chunks?: ReactNode | null): ReactNode => (
        <Typography component="span" color="info.main" fontWeight={fontWeightBold}>
          {mapChunks(chunks)}
        </Typography>
      ),
      // eslint-disable-next-line react/no-unstable-nested-components
      par: (chunks?: ReactNode | null): ReactNode => (
        <Typography component="div" variant="body1" fontStyle="italic">
          {mapChunks(chunks)}
        </Typography>
      ),
      // eslint-disable-next-line react/no-unstable-nested-components
      link: (chunks?: ReactNode | null): ReactNode => (
        <StandardLink href={link} onClick={handleLinkClick}>
          {mapChunks(chunks)}
        </StandardLink>
      )
    });
    return !isString(msg) || size(trim(msg)) >= 1 ? msg : null;
  }, [customData, link, handleLinkClick, formatMessage]);

  return message ? (
    <CardSection top>
      {message}
    </CardSection>
  ) : null;
};

export default memo(RoleCustomData);
